// Port of ios/Sources/Models/PresenceOverride.swift.

/**
 * A system administrator's presence pin (shared/PLUGINS.md §8.1.1), served by the
 * `satoritech-device-indicator` plugin at `/plugins/satoritech-device-indicator/presence/override`.
 * The SERVER enforces both halves for every viewer — webapp included — so the client only reads
 * and writes the two flags; nothing here changes what this device reports about itself.
 *
 * Desktop implies online (being shown on the computer IS being shown connected), so the two
 * switches move together: turning desktop on turns online on, turning online off turns desktop
 * off. Twin of Android's `PresenceOverride`.
 */
export interface PresenceOverride {
  alwaysOnline: boolean;
  alwaysDesktop: boolean;
}

export const none: PresenceOverride = { alwaysOnline: false, alwaysDesktop: false };

export function withAlwaysOnline(v: PresenceOverride, on: boolean): PresenceOverride {
  return on ? { alwaysOnline: true, alwaysDesktop: v.alwaysDesktop } : { ...none };
}

export function withAlwaysDesktop(v: PresenceOverride, on: boolean): PresenceOverride {
  return on ? { alwaysOnline: true, alwaysDesktop: true } : { alwaysOnline: v.alwaysOnline, alwaysDesktop: false };
}

export function wireBody(v: PresenceOverride): { always_online: boolean; always_desktop: boolean } {
  return { always_online: v.alwaysOnline, always_desktop: v.alwaysDesktop };
}

/**
 * `allowed` is the server's own answer to "may this reader set it"; a refused reader never sees
 * flags, whatever is stored. Takes the parsed JSON (or the raw text).
 */
export function decode(json: unknown): PresenceOverride | null {
  let obj = json;
  if (typeof obj === 'string') {
    try {
      obj = JSON.parse(obj);
    } catch {
      return null;
    }
  }
  if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) return null;
  const o = obj as Record<string, unknown>;
  if (o.allowed !== true) return null;
  return { alwaysOnline: o.always_online === true, alwaysDesktop: o.always_desktop === true };
}

export function equal(a: PresenceOverride | null, b: PresenceOverride | null): boolean {
  if (!a || !b) return a === b;
  return a.alwaysOnline === b.alwaysOnline && a.alwaysDesktop === b.alwaysDesktop;
}

export const PresenceOverrides = { none, withAlwaysOnline, withAlwaysDesktop, wireBody, decode, equal } as const;
